import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Subscription, Observable } from 'rxjs';
import { debounceTime, filter, mergeMap } from 'rxjs/operators';
import { Table } from '@cureskin/api-client';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { ConnectionService } from '../../../../services/connection-service';
import { AppConfig } from '../../../app.config';
import { DirectivesModule } from '../../../../components/directives.module';
import { LanguageStringDialogComponent } from './language-string-dialog.component';
import { Broadcaster } from '../../../../components/broadcaster';
import { ApiClientConstant } from '@cureskin/api-client';
import { InputType } from '../../../../../typings/client/input';

@Component({
  selector: 'product-collection-edit',
  templateUrl: './product-collection-edit.html',
  styleUrls: ['./product-collection-edit.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DirectivesModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatInputModule
  ]
})
export class ProductCollectionEditComponent implements OnInit, OnDestroy {
  productCollection: Table.ProductCollection;
  subscriptions: Array<Subscription>;
  title: string = '';
  description: string = '';
  originalTitleLanguageStringId: string = '';
  originalDescriptionLanguageStringId: string = '';
  originalProductIds: Array<string> = [];
  products: Array<any> = [];
  autoCompleteProductController: UntypedFormControl = new UntypedFormControl();
  productOptions: Observable<Array<{ name: string; object: any }>>;
  displayTypeOptions: Array<InputType.SelectOption> = [
    { value: 'STANDARD', display: 'Standard' },
    { value: 'PURPOSE_HEADER_BANNER', display: 'Purpose Header Banner' },
    { value: 'ENLARGED_CARDS', display: 'Enlarged Cards' },
    { value: 'BANNER', display: 'Banner' },
    { value: 'PURPOSE_SUBTEXT_BANNER', display: 'Purpose Subtext Banner' },
    { value: 'TAGGED_CARDS', display: 'Tagged Cards' },
  ];
  protected readonly apiClientConstant: typeof ApiClientConstant = ApiClientConstant;
  constructor(
    private router: Router,
    private conn: ConnectionService,
    private route: ActivatedRoute,
    public appConfig: AppConfig,
    private dialog: MatDialog,
    private broadcaster: Broadcaster,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions = [];
    this.products = [];
    this.autoCompleteProductController.setValue('');
    this.productOptions = this.autoCompleteProductController.valueChanges
      .pipe(
        debounceTime(300),
        filter((token: string) => !!token && token.length > 0),
        mergeMap((token: string) => this.getProducts(token)));
    this.subscribeToUrlQueryParams();
  }

  subscribeToUrlQueryParams(): void {
    this.subscriptions.push(this.route.params.subscribe(() => {
      if (this.route.snapshot.params.name) {
        this.conn.serverApi.findProductCollectionByName(this.route.snapshot.params.name, this.conn.getCurrentUser().getSessionToken())
          .then(async (data: any) => {
            this.productCollection = data.data.productCollection;
            this.originalTitleLanguageStringId = this.productCollection['titleLanguageString']?.objectId || '';
            this.originalDescriptionLanguageStringId = this.productCollection['descriptionLanguageString']?.objectId || '';

            if (this.productCollection['titleLanguageString']?.objectId) {
              this.title = await this.getLanguageString(this.productCollection['titleLanguageString']['objectId']);
            }

            if (this.productCollection['descriptionLanguageString']?.objectId) {
              this.description = await this.getLanguageString(this.productCollection['descriptionLanguageString']['objectId']);
            }

            if (this.productCollection['products'] && Array.isArray(this.productCollection['products'])) {
              this.products = [...this.productCollection['products']];
              this.originalProductIds = this.products.map((p: any) => p.id || p.objectId || p.get?.('objectId'));
              await this.loadProductTitles();
            } else {
              this.products = [];
              this.originalProductIds = [];
            }
          })
          .catch((error: any) => {
            console.error('Error loading product collection:', error);
          });
      } else {
        this.initializeNewProductCollection();
      }
    }));
  }

  initializeNewProductCollection(): void {
    this.productCollection = {
      name: '',
      priority: 0,
      isActive: true,
      imageUrl: '',
      productImageType: 'PRODUCT_IMAGE',
      displayType: 'STANDARD',
    } as any;
    this.products = [];
    this.originalProductIds = [];
    this.title = '';
    this.description = '';
    this.originalTitleLanguageStringId = '';
    this.originalDescriptionLanguageStringId = '';
  }
  async getLanguageString(id: string): Promise<string> {
    const data = await this.conn.getLanguageString(id);
    return data?.get('en');
  }

  async loadProductTitles(): Promise<void> {
    for (const product of this.products) {
      await this.loadProductTitle(product);
    }
  }

  async getProducts(name: string): Promise<Array<{ name: string; object: any }>> {
    if (!name || name.length < 1) {
      return [];
    }
    const products = await this.conn.findCatalogs({
      where: {
        title: { $regex: name, $options: 'i' },
        inventoryStatus: ['AVAILABLE', 'UNAVAILABLE', 'RESTRICTED'],
      },
      include: ['titleLanguageString'],
      project: ['title', 'quantity', 'quantityUnit', 'type', 'margUnit', 'mrp', 'price', 'titleLanguageString'],
      ascending: 'margUnit',
      limit: 10,
    });
    return products.map((product: any): { name: string; object: any } => ({
      name: this.getProductDisplayName(product),
      object: product,
    }));
  }

  getProductDisplayName(product: any): string {
    const title = product.get('title');
    const quantity = product.get('quantity')
    const quantityUnit = product.get('quantityUnit');
    const margUnit = product.get('margUnit');
    const type = product.get('type');
    return `${title} [ ${
      (margUnit || 1) > 1
        ? `${margUnit} sheets, `
        : ''
    }${quantity}${quantityUnit} ] ${type === 'sample' ? '(sample)' : ''}`;
  }

  async autoCompleteOnProductSelect(item: { name: string; object: any }): Promise<void> {
    const productId = item.object.id || item.object.objectId || item.object.get?.('objectId');
    // Check if product is already in the list
    const isDuplicate = this.products.some((p: any) => {
      const pId = p.id || p.objectId || p.get?.('objectId');
      return pId === productId;
    });

    if (!isDuplicate) {
      const product = item.object;
      // Load title for the new product - titleLanguageString should be included from findCatalogs
      await this.loadProductTitle(product);
      this.products.push(product);
    }
    this.autoCompleteProductController.setValue('');
  }

  async loadProductTitle(product: any): Promise<void> {
    const titleLanguageString = product.get ? product.get('titleLanguageString') : product.titleLanguageString;
    if (titleLanguageString) {
      const titleLanguageStringId = titleLanguageString.objectId || titleLanguageString.id || (titleLanguageString.get ? titleLanguageString.get('objectId') : null);
      if (titleLanguageStringId) {
        try {
          const titleText = await this.getLanguageString(titleLanguageStringId);
          if (product.get) {
            product._displayTitle = titleText;
          } else {
            product.displayTitle = titleText;
          }
        } catch (error) {
          console.error('Error loading product title:', error);
          if (product.get) {
            product._displayTitle = 'Loading...';
          } else {
            product.displayTitle = 'Loading...';
          }
        }
      } else {
        const title = product.get ? product.get('title') : product.title;
        if (product.get) {
          product._displayTitle = title || 'No title';
        } else {
          product.displayTitle = title || 'No title';
        }
      }
    } else {
      const title = product.get ? product.get('title') : product.title;
      if (product.get) {
        product._displayTitle = title || 'No title';
      } else {
        product.displayTitle = title || 'No title';
      }
    }
  }

  removeProduct(index: number): void {
    this.products.splice(index, 1);
  }

  async editLanguageString(type: string): Promise<void> {
    if (!this.productCollection) {
      this.initializeNewProductCollection();
    }

    const currentLanguageStringId = this.productCollection[type]?.objectId;
    const currentValue = type === 'titleLanguageString' ? this.title : this.description;
    const tags = type === 'titleLanguageString' ? ['product-collection-title'] : ['product-collection-description'];

    const dialogRef = this.dialog.open(LanguageStringDialogComponent, {
      width: '600px',
      data: {
        currentLanguageStringId,
        tags,
        searchValue: currentValue,
      },
    });

    dialogRef.afterClosed().subscribe((selectedLanguageString: any) => {
      if (!selectedLanguageString) return;
      this.productCollection[type] = {
        objectId: selectedLanguageString.id,
        className: 'LanguageString',
        __type: 'Pointer',
      };

      if (type === 'titleLanguageString') {
        this.title = selectedLanguageString.get('en');
      } else {
        this.description = selectedLanguageString.get('en');
      }
    });
  }

  async saveProductCollection(): Promise<void> {
    if (!this.productCollection) {
      this.broadcaster.broadcast('NOTIFY', {
        message: 'Product collection not loaded',
        type: this.apiClientConstant.Toast.Type.ERROR,
      });
      return;
    }

    try {
      const isNew = !this.productCollection['objectId'];
      const sessionToken = this.conn.getCurrentUser().getSessionToken();
      const saveData: any = {
        name: this.productCollection['name'],
        priority: this.productCollection['priority'] || 0,
        isActive: this.productCollection['isActive'] !== undefined ? this.productCollection['isActive'] : true,
      };

      // Add optional fields if they exist
      if (this.productCollection['imageUrl']) {
        saveData.imageUrl = this.productCollection['imageUrl'];
      }
      if (this.productCollection['productImageType']) {
        saveData.productImageType = this.productCollection['productImageType'];
      }
      if (this.productCollection['displayType']) {
        saveData.displayType = this.productCollection['displayType'];
      }

      // Add language strings
      const currentTitleLanguageStringId = this.productCollection['titleLanguageString']?.objectId;
      if (currentTitleLanguageStringId) {
        if (isNew || currentTitleLanguageStringId !== this.originalTitleLanguageStringId) {
          saveData.titleLanguageString = {
            objectId: currentTitleLanguageStringId,
            className: 'LanguageString',
            __type: 'Pointer',
          };
        }
      }

      const currentDescriptionLanguageStringId = this.productCollection['descriptionLanguageString']?.objectId;
      if (currentDescriptionLanguageStringId) {
        if (isNew || currentDescriptionLanguageStringId !== this.originalDescriptionLanguageStringId) {
          saveData.descriptionLanguageString = {
            objectId: currentDescriptionLanguageStringId,
            className: 'LanguageString',
            __type: 'Pointer',
          };
        }
      }

      // Add products
      if (this.products.length > 0) {
        saveData.products = this.products.map((product: any) => ({
          objectId: product.id || product.objectId || product.get?.('objectId'),
          className: 'Catalog',
          __type: 'Pointer',
        }));
      } else if (!isNew) {
        // For updates, if products array is empty, we should still include it to clear existing products
        const currentProductIds = this.products.map((p: any) => p.id || p.objectId || p.get?.('objectId'));
        const productsChanged = JSON.stringify(currentProductIds.sort()) !== JSON.stringify(this.originalProductIds.sort());
        if (productsChanged) {
          saveData.products = [];
        }
      }

      if (isNew) {
        // Create new product collection
        await this.conn.serverApi.createProductCollection(saveData, sessionToken);
        this.broadcaster.broadcast('NOTIFY', {
          message: 'Product collection created successfully',
          type: this.apiClientConstant.Toast.Type.SUCCESS,
        });
      } else {
        // Update existing product collection
        await this.conn.serverApi.updateProductCollection(
          this.productCollection['objectId'],
          saveData,
          sessionToken
        );
        this.broadcaster.broadcast('NOTIFY', {
          message: 'Product collection updated successfully',
          type: this.apiClientConstant.Toast.Type.SUCCESS,
        });
      }

      this.router.navigate(['/productCollections/getAllProductCollections']);
    } catch (error: any) {
      this.broadcaster.broadcast('NOTIFY', {
        message: error.message || `Error ${this.productCollection['objectId'] ? 'updating' : 'creating'} product collection`,
        type: this.apiClientConstant.Toast.Type.ERROR,
      });
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }
  }
}

